// SCR-STAFF-880 — کدینگ و تفصیلی‌ها (US-ACC-001، 002، 005): ساختار پویای سطوح کد (طول، الگو، جداکننده، نمونه زنده)، سطوح تفصیلی شناور،
// فهرست حساب‌ها با افزودن و غیرفعال‌سازی، ورود CSV یا Excel (همه یا هیچ؛ اجرای آزمایشی)، تفصیلی‌ها و کتابخانه الگو (نمونه با نشان «نمونه»).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type AccAccountNature,
  type AccAccountView,
  type AccChartImportResponse,
  type AccChartTemplateView,
  type AccCodeLevel,
  type AccCodePattern,
  type AccDetailAccountType,
  type AccDetailAccountView,
  type AccDetailDimension,
  type AccDetailLevel,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  ListPage,
  LoadingState,
  SampleBadge,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { accKeys, fileToBase64, num, SCR_STAFF_880 } from './common';

const NATURES: readonly AccAccountNature[] = ['Debit', 'Credit', 'Both'];
const PATTERNS: readonly AccCodePattern[] = ['Numeric', 'Alphanumeric'];
const DIMENSIONS: readonly AccDetailDimension[] = ['Party', 'Branch', 'CostCenter', 'Project', 'Custom'];
const DETAIL_TYPES: readonly AccDetailAccountType[] = [
  'Supplier',
  'Provider',
  'Insurer',
  'Organization',
  'CostCenter',
  'Project',
  'Custom',
];

type TabId = 'structure' | 'accounts' | 'details' | 'templates';

export function ChartPage() {
  const { t } = useTranslation('acc');
  const [tab, setTab] = useState<TabId>('structure');
  return (
    <ListPage screenId={SCR_STAFF_880} titleKey="acc:chart.title" subtitleKey="acc:chart.subtitle">
      <Tabs
        value={tab}
        onChange={(_event, value: TabId) => setTab(value)}
        aria-label={t('acc:chart.title')}
        sx={{ mb: 2 }}
      >
        <Tab value="structure" label={t('acc:chart.tab_structure')} data-testid="acc-tab-structure" />
        <Tab value="accounts" label={t('acc:chart.tab_accounts')} data-testid="acc-tab-accounts" />
        <Tab value="details" label={t('acc:chart.tab_details')} data-testid="acc-tab-details" />
        <Tab value="templates" label={t('acc:chart.tab_templates')} data-testid="acc-tab-templates" />
      </Tabs>
      {tab === 'structure' ? <StructureTab /> : null}
      {tab === 'accounts' ? <AccountsTab /> : null}
      {tab === 'details' ? <DetailsTab /> : null}
      {tab === 'templates' ? <TemplatesTab /> : null}
    </ListPage>
  );
}

/** نمونه کد زنده سطح (همان منطق ChartStructure.Sample). */
function sampleOf(levels: readonly AccCodeLevel[]): string {
  return levels
    .map((level, index) => {
      const digits = Array.from({ length: Math.max(0, num(level.length)) }, (_v, i) =>
        level.pattern === 'Alphanumeric' && i === 0 ? 'A' : String((i + index + 1) % 10),
      );
      return digits.join('') + (index < levels.length - 1 ? (level.separator ?? '') : '');
    })
    .join('');
}

function StructureTab() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const canManage = access.accessMode === 'Full' && access.can('acc.chart.manage');
  const structure = useQuery({
    queryKey: accKeys.structure(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/structure')),
  });
  const [levels, setLevels] = useState<AccCodeLevel[]>([]);
  const [details, setDetails] = useState<AccDetailLevel[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (structure.data && !dirty) {
      setLevels(
        structure.data.levels.map((level) => ({
          name: level.name,
          length: level.length,
          pattern: level.pattern,
          separator: level.separator ?? null,
        })),
      );
      setDetails(structure.data.detailLevels.map((level) => ({ ...level })));
    }
  }, [structure.data, dirty]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/accounting/chart/structure', { body: { levels, detailLevels: details } }),
      ),
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
    },
  });
  if (structure.isPending) {
    return <LoadingState rows={3} />;
  }
  if (structure.error) {
    return <ErrorState error={structure.error} onRetry={() => void structure.refetch()} />;
  }
  const edit = (index: number, patch: Partial<AccCodeLevel>) => {
    setDirty(true);
    setLevels((current) => current.map((level, i) => (i === index ? { ...level, ...patch } : level)));
  };
  const editDetail = (index: number, patch: Partial<AccDetailLevel>) => {
    setDirty(true);
    setDetails((current) => current.map((level, i) => (i === index ? { ...level, ...patch } : level)));
  };
  return (
    <Stack spacing={2}>
      {!structure.data.defined ? (
        <Alert severity="info" data-testid="acc-no-structure">
          {t('acc:chart.no_structure')}
        </Alert>
      ) : null}
      {structure.data.hasActivity ? <Alert severity="warning">{t('acc:chart.has_activity')}</Alert> : null}
      {save.error ? <ErrorState error={save.error} compact /> : null}
      {save.isSuccess && !dirty ? <Alert severity="success">{t('acc:chart.saved')}</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('acc:chart.levels')}
        </Typography>
        <Stack spacing={1}>
          {levels.map((level, index) => (
            <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} data-testid="acc-level">
              <TextField
                size="small"
                label={t('acc:chart.level_name')}
                value={level.name}
                disabled={!canManage}
                onChange={(event) => edit(index, { name: event.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'acc-level-name' } }}
              />
              <TextField
                size="small"
                type="number"
                label={t('acc:chart.length')}
                value={num(level.length)}
                disabled={!canManage}
                onChange={(event) => edit(index, { length: Number(event.target.value) })}
                slotProps={{ htmlInput: { min: 1, max: 10, 'data-testid': 'acc-level-length' } }}
                sx={{ width: 100 }}
              />
              <TextField
                select
                size="small"
                label={t('acc:chart.pattern')}
                value={level.pattern}
                disabled={!canManage}
                onChange={(event) => edit(index, { pattern: event.target.value as AccCodePattern })}
                sx={{ minWidth: 140 }}
              >
                {PATTERNS.map((pattern) => (
                  <MenuItem key={pattern} value={pattern}>
                    {t(`acc:pattern.${pattern}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t('acc:chart.separator')}
                value={level.separator ?? ''}
                disabled={!canManage}
                onChange={(event) => edit(index, { separator: event.target.value || null })}
                sx={{ width: 100 }}
                slotProps={{ htmlInput: { maxLength: 1, dir: 'ltr' } }}
              />
              {canManage ? (
                <IconButton
                  aria-label={t('acc:chart.remove')}
                  onClick={() => {
                    setDirty(true);
                    setLevels((current) => current.filter((_l, i) => i !== index));
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              ) : null}
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          {canManage ? (
            <Button
              size="small"
              onClick={() => {
                setDirty(true);
                setLevels((current) => [
                  ...current,
                  { name: '', length: 2, pattern: 'Numeric', separator: null },
                ]);
              }}
              disabled={levels.length >= 10}
              data-testid="acc-level-add"
            >
              {t('acc:chart.add_level')}
            </Button>
          ) : null}
          <Typography variant="body2">
            {t('acc:chart.sample_code')}:{' '}
            <bdi dir="ltr" data-testid="acc-sample-code">
              {sampleOf(levels)}
            </bdi>
          </Typography>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('acc:chart.detail_levels')}
        </Typography>
        <Stack spacing={1}>
          {details.map((level, index) => (
            <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                label={t('acc:chart.level_name')}
                value={level.name}
                disabled={!canManage}
                onChange={(event) => editDetail(index, { name: event.target.value })}
              />
              <TextField
                select
                size="small"
                label={t('acc:chart.detail_type')}
                value={level.dimension}
                disabled={!canManage}
                onChange={(event) =>
                  editDetail(index, { dimension: event.target.value as AccDetailDimension })
                }
                sx={{ minWidth: 160 }}
              >
                {DIMENSIONS.map((dimension) => (
                  <MenuItem key={dimension} value={dimension}>
                    {t(`acc:dimension.${dimension}`)}
                  </MenuItem>
                ))}
              </TextField>
              {canManage ? (
                <IconButton
                  aria-label={t('acc:chart.remove')}
                  onClick={() => {
                    setDirty(true);
                    setDetails((current) => current.filter((_l, i) => i !== index));
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              ) : null}
            </Stack>
          ))}
        </Stack>
        {canManage && details.length < 3 ? (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setDirty(true);
              setDetails((current) => [...current, { name: '', dimension: 'Party' }]);
            }}
          >
            {t('acc:chart.add_detail_level')}
          </Button>
        ) : null}
      </Paper>
      {canManage ? (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            data-testid="acc-structure-save"
          >
            {t('acc:common.save')}
          </Button>
          <Button disabled={!dirty} onClick={() => setDirty(false)}>
            {t('acc:common.cancel')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function AccountsTab() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canManage = access.accessMode === 'Full' && access.can('acc.chart.manage');
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<{ code: string; title: string; nature: AccAccountNature }>({
    code: '',
    title: '',
    nature: 'Debit',
  });
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState({ code: 0, title: 1, nature: '' });
  const [hasHeader, setHasHeader] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<AccChartImportResponse | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/chart/accounts', {
          body: { code: form.code.trim(), title: form.title.trim(), nature: form.nature },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setAdding(false);
      setForm({ code: '', title: '', nature: 'Debit' });
      refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (account: AccAccountView) =>
      unwrap(
        tenantApi.PUT('/api/v1/accounting/chart/accounts/{id}', {
          params: { path: { id: account.id } },
          body: { title: account.title, nature: account.nature, isActive: !account.isActive },
        }),
      ),
    onSuccess: refresh,
  });
  const runImport = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('file');
      }
      const contentBase64 = await fileToBase64(file);
      return unwrap(
        tenantApi.POST('/api/v1/accounting/chart/accounts:import', {
          body: {
            fileName: file.name,
            contentBase64,
            columns: {
              code: columns.code,
              title: columns.title,
              nature: columns.nature === '' ? null : Number(columns.nature),
            },
            hasHeader,
            dryRun,
          },
        }),
      );
    },
    onSuccess: (data) => {
      setResult(data);
      if (!data.dryRun && data.errors.length === 0) {
        refresh();
      }
    },
  });
  const tableColumns: DataTableColumn<AccAccountView>[] = [
    { id: 'code', headerKey: 'acc:common.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'title',
      headerKey: 'acc:common.title',
      cell: (row) => <span style={{ paddingInlineStart: (num(row.level) - 1) * 16 }}>{row.title}</span>,
    },
    { id: 'level', headerKey: 'acc:common.level', cell: (row) => faDigits(num(row.level)) },
    { id: 'nature', headerKey: 'acc:common.nature', cell: (row) => t(`acc:nature.${row.nature}`) },
    { id: 'source', headerKey: 'acc:chart.platform', cell: (row) => t(`acc:source.${row.source}`) },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <StatusChip
          tone={row.isActive ? 'active' : 'inactive'}
          labelKey={row.isActive ? 'acc:common.active' : 'acc:common.inactive'}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => toggle.mutate(row)} data-testid="acc-account-toggle">
            {row.isActive ? t('acc:common.inactive') : t('acc:common.active')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      {canManage ? (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => setAdding(true)} data-testid="acc-account-add">
            {t('acc:chart.add_account')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setResult(null);
              setImporting(true);
            }}
            data-testid="acc-account-import"
          >
            {t('acc:chart.import')}
          </Button>
        </Stack>
      ) : null}
      {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
      <DataTable
        captionKey="acc:chart.tab_accounts"
        columns={tableColumns}
        rows={accounts.data?.items}
        getRowId={(row) => row.id}
        isLoading={accounts.isPending}
        error={accounts.error}
        onRetry={() => void accounts.refetch()}
        emptyTitleKey="acc:chart.empty_accounts"
      />
      <FormDialog
        open={adding}
        titleKey="acc:chart.add_account"
        onSubmit={() => create.mutate()}
        onClose={() => setAdding(false)}
        isDirty={form.code !== '' || form.title !== ''}
        isSubmitting={create.isPending}
        submitError={create.error}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('acc:chart.account_code')}
            value={form.code}
            onChange={(event) => setForm({ ...form, code: event.target.value })}
            required
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'acc-account-code' } }}
          />
          <TextField
            label={t('acc:chart.account_title')}
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            required
            slotProps={{ htmlInput: { 'data-testid': 'acc-account-title' } }}
          />
          <TextField
            select
            label={t('acc:common.nature')}
            value={form.nature}
            onChange={(event) => setForm({ ...form, nature: event.target.value as AccAccountNature })}
          >
            {NATURES.map((nature) => (
              <MenuItem key={nature} value={nature}>
                {t(`acc:nature.${nature}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </FormDialog>
      <FormDialog
        open={importing}
        titleKey="acc:chart.import"
        onSubmit={() => runImport.mutate()}
        onClose={() => setImporting(false)}
        isDirty={file !== null}
        isSubmitting={runImport.isPending}
        submitError={runImport.error}
        submitKey={dryRun ? 'acc:chart.dry_run' : 'acc:common.apply'}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            type="file"
            label={t('acc:chart.import_file')}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { accept: '.csv,.xlsx', 'data-testid': 'acc-import-file' },
            }}
            onChange={(event) => setFile((event.target as HTMLInputElement).files?.[0] ?? null)}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="number"
              label={t('acc:chart.code_column')}
              value={columns.code}
              onChange={(event) => setColumns({ ...columns, code: Number(event.target.value) })}
            />
            <TextField
              size="small"
              type="number"
              label={t('acc:chart.title_column')}
              value={columns.title}
              onChange={(event) => setColumns({ ...columns, title: Number(event.target.value) })}
            />
            <TextField
              size="small"
              type="number"
              label={t('acc:chart.nature_column')}
              value={columns.nature}
              onChange={(event) => setColumns({ ...columns, nature: event.target.value })}
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox checked={hasHeader} onChange={(event) => setHasHeader(event.target.checked)} />
            }
            label={t('acc:chart.has_header')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dryRun}
                onChange={(event) => setDryRun(event.target.checked)}
                slotProps={{ input: { 'aria-label': t('acc:chart.dry_run') } }}
              />
            }
            label={t('acc:chart.dry_run')}
          />
          {result ? (
            <Alert severity={result.errors.length > 0 ? 'error' : 'success'} data-testid="acc-import-result">
              {t('acc:chart.import_result', {
                created: faDigits(num(result.created)),
                skipped: faDigits(num(result.skipped)),
              })}
              {result.errors.length > 0 ? (
                <ul>
                  {result.errors.map((error) => (
                    <li key={`${error.row}-${error.cause}`}>
                      {t('acc:chart.row')} {faDigits(num(error.row))}: {error.cause}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}
        </Stack>
      </FormDialog>
    </Stack>
  );
}

function DetailsTab() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canManage = access.accessMode === 'Full' && access.can('acc.chart.manage');
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ type: AccDetailAccountType; code: string; title: string }>({
    type: 'Supplier',
    code: '',
    title: '',
  });
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/detail-accounts', {
          body: { type: form.type, code: form.code.trim() || null, title: form.title.trim() },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setOpen(false);
      setForm({ type: 'Supplier', code: '', title: '' });
      void queryClient.invalidateQueries({ queryKey: accKeys.details(session?.tenantId) });
    },
  });
  const columns: DataTableColumn<AccDetailAccountView>[] = [
    { id: 'code', headerKey: 'acc:common.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'title', headerKey: 'acc:common.title', cell: (row) => row.title },
    { id: 'type', headerKey: 'acc:chart.detail_type', cell: (row) => t(`acc:detail_type.${row.type}`) },
    {
      id: 'auto',
      headerKey: 'acc:chart.automatic',
      cell: (row) => (row.isAutomatic ? t('acc:chart.automatic') : '—'),
    },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <StatusChip
          tone={row.isActive ? 'active' : 'inactive'}
          labelKey={row.isActive ? 'acc:common.active' : 'acc:common.inactive'}
        />
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      {canManage ? (
        <Button
          variant="contained"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => setOpen(true)}
          data-testid="acc-detail-add"
        >
          {t('acc:chart.add_detail')}
        </Button>
      ) : null}
      <DataTable
        captionKey="acc:chart.tab_details"
        columns={columns}
        rows={details.data?.items}
        getRowId={(row) => row.id}
        isLoading={details.isPending}
        error={details.error}
        onRetry={() => void details.refetch()}
        emptyTitleKey="acc:chart.empty_details"
      />
      <FormDialog
        open={open}
        titleKey="acc:chart.add_detail"
        onSubmit={() => create.mutate()}
        onClose={() => setOpen(false)}
        isDirty={form.title !== ''}
        isSubmitting={create.isPending}
        submitError={create.error}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t('acc:chart.detail_type')}
            value={form.type}
            onChange={(event) => setForm({ ...form, type: event.target.value as AccDetailAccountType })}
          >
            {DETAIL_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`acc:detail_type.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('acc:common.code')}
            value={form.code}
            onChange={(event) => setForm({ ...form, code: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr' } }}
          />
          <TextField
            label={t('acc:common.title')}
            value={form.title}
            required
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'acc-detail-title' } }}
          />
        </Stack>
      </FormDialog>
    </Stack>
  );
}

function TemplatesTab() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canManage = access.accessMode === 'Full' && access.can('acc.chart.manage');
  const templates = useQuery({
    queryKey: accKeys.templates(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/templates')),
  });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: '', name: '' });
  const [applied, setApplied] = useState<number | null>(null);
  const apply = useMutation({
    mutationFn: (code: string) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/chart/templates/{code}:apply', { params: { path: { code } } }),
      ),
    onSuccess: (data) => {
      setApplied(num(data.accountsCreated));
      void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
    },
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/chart/templates', {
          body: { code: form.code.trim(), name: form.name.trim() },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setSaving(false);
      void queryClient.invalidateQueries({ queryKey: accKeys.templates(session?.tenantId) });
    },
  });
  const columns: DataTableColumn<AccChartTemplateView>[] = [
    {
      id: 'name',
      headerKey: 'acc:chart.template_name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.name}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'code', headerKey: 'acc:chart.template_code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'kind',
      headerKey: 'acc:common.status',
      cell: (row) => (row.isPlatform ? t('acc:chart.platform') : t('acc:chart.own')),
    },
    {
      id: 'count',
      headerKey: 'acc:chart.tab_accounts',
      cell: (row) => t('acc:chart.account_count', { count: num(row.accountCount) }),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button
            size="small"
            onClick={() => apply.mutate(row.code)}
            data-testid="acc-template-apply"
            data-code={row.code}
          >
            {t('acc:chart.apply_template')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      {applied !== null ? (
        <Alert severity="success" data-testid="acc-template-applied">
          {t('acc:chart.applied', { count: faDigits(applied) })}
        </Alert>
      ) : null}
      {apply.error ? <ErrorState error={apply.error} compact /> : null}
      {canManage ? (
        <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => setSaving(true)}>
          {t('acc:chart.save_template')}
        </Button>
      ) : null}
      <DataTable
        captionKey="acc:chart.tab_templates"
        columns={columns}
        rows={templates.data?.items}
        getRowId={(row) => row.code}
        isLoading={templates.isPending}
        error={templates.error}
        onRetry={() => void templates.refetch()}
        emptyTitleKey="acc:chart.empty_accounts"
      />
      <FormDialog
        open={saving}
        titleKey="acc:chart.save_template"
        onSubmit={() => save.mutate()}
        onClose={() => setSaving(false)}
        isDirty={form.code !== ''}
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('acc:chart.template_code')}
            value={form.code}
            required
            onChange={(event) => setForm({ ...form, code: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr' } }}
          />
          <TextField
            label={t('acc:chart.template_name')}
            value={form.name}
            required
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Stack>
      </FormDialog>
    </Stack>
  );
}
